/**
 * Largest Triangle Three Buckets (LTTB) downsampling algorithm
 *
 * This algorithm reduces the number of data points while preserving the visual
 * characteristics of the time series. It's specifically designed for line chart
 * visualization and outperforms simple methods like averaging or sampling.
 *
 * Reference: Sveinn Steinarsson, "Downsampling Time Series for Visual Representation"
 * https://skemman.is/bitstream/1946/15343/3/SS_MSthesis.pdf
 */

export type DataPoint = readonly [time: number, value: number | null];

/**
 * Downsample a time series using the LTTB algorithm
 *
 * @param data - Array of [timestamp, value] tuples
 * @param threshold - Target number of points (will return at most this many)
 * @returns Downsampled array preserving visual characteristics
 */
export function downsampleLTTB(data: readonly DataPoint[], threshold: number): DataPoint[] {
	const dataLength = data.length;
	console.log("downsampleLTTB");
	// No need to downsample if data is smaller than threshold
	if (threshold >= dataLength || threshold <= 2) {
		return [...data];
	}

	// Filter out null values for LTTB calculation, but track original indices
	const validData: Array<{ index: number; point: DataPoint }> = [];
	for (let i = 0; i < dataLength; i++) {
		if (data[i][1] !== null) {
			validData.push({ index: i, point: data[i] });
		}
	}

	const validLength = validData.length;

	// If valid points are less than threshold, return all valid points
	if (validLength <= threshold) {
		return validData.map(d => d.point);
	}

	const sampled: DataPoint[] = [];

	// Always include the first point
	sampled.push(validData[0].point);

	// Bucket size (excluding first and last points)
	const bucketSize = (validLength - 2) / (threshold - 2);

	let previousSelectedIndex = 0;

	for (let i = 0; i < threshold - 2; i++) {
		// Calculate bucket boundaries
		const bucketStart = Math.floor((i + 1) * bucketSize) + 1;
		const bucketEnd = Math.floor((i + 2) * bucketSize) + 1;
		const actualBucketEnd = Math.min(bucketEnd, validLength - 1);

		// Calculate the average point in the next bucket (for area calculation)
		const nextBucketStart = actualBucketEnd;
		const nextBucketEnd = Math.min(Math.floor((i + 3) * bucketSize) + 1, validLength);

		let avgX = 0;
		let avgY = 0;
		let avgCount = 0;

		for (let j = nextBucketStart; j < nextBucketEnd; j++) {
			avgX += validData[j].point[0];
			avgY += validData[j].point[1] as number;
			avgCount++;
		}

		if (avgCount > 0) {
			avgX /= avgCount;
			avgY /= avgCount;
		} else {
			// Fallback to last point if next bucket is empty
			avgX = validData[validLength - 1].point[0];
			avgY = validData[validLength - 1].point[1] as number;
		}

		// Get the previous selected point
		const prevPoint = validData[previousSelectedIndex].point;
		const prevX = prevPoint[0];
		const prevY = prevPoint[1] as number;

		// Find the point in the current bucket that forms the largest triangle
		let maxArea = -1;
		let selectedIndex = bucketStart;

		for (let j = bucketStart; j < actualBucketEnd; j++) {
			const currentPoint = validData[j].point;
			const currentX = currentPoint[0];
			const currentY = currentPoint[1] as number;

			// Calculate triangle area using the cross product formula
			// Area = 0.5 * |x1(y2 - y3) + x2(y3 - y1) + x3(y1 - y2)|
			const area = Math.abs((prevX - avgX) * (currentY - prevY) - (prevX - currentX) * (avgY - prevY));

			if (area > maxArea) {
				maxArea = area;
				selectedIndex = j;
			}
		}

		sampled.push(validData[selectedIndex].point);
		previousSelectedIndex = selectedIndex;
	}

	// Always include the last point
	sampled.push(validData[validLength - 1].point);

	return sampled;
}

/**
 * Default threshold for chart visualization
 * 3000 points provides good detail while maintaining smooth performance
 */
export const DEFAULT_DOWNSAMPLE_THRESHOLD = 3000;
